import { SerializedNodes } from '@craftjs/core';
import { CalculatorControlsResolver } from './expression-helper';
import i18next from 'i18next';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { executeSimpleExpression } from './executor';
import { BaseVariableSection } from 'src/services/api/api-client';

interface ExpressionFunction {
  name: string;
  signature: string; // the whole signature with parameters
  func: (...args: any[]) => any;
}

export const getBaseExecutionContext = (): Record<string, any> =>
  expressionFunctions.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.func }), {});

function convertToBool(value: any): boolean {
  return !!value;
}

export const expressionFunctions: ExpressionFunction[] = [
  {
    name: 'iif',
    signature: 'iif(value: boolean, trueValue: number, falseValue: number)',
    func: iif,
  },
  {
    name: 'not',
    signature: 'not(value: boolean)',
    func: not,
  },
  {
    name: 'fieldValue',
    signature: 'fieldValue(dataKey: SingleValueDataKey): number',
    func: fieldValue,
  },
  {
    name: 'isEmpty',
    signature: 'isEmpty(dataKey: FieldDataKey): boolean',
    func: isEmpty,
  },
  {
    name: 'getVariable',
    signature: 'getVariable(name: Variable): number',
    func: getVariable,
  },
  {
    name: 'fieldValues',
    signature: 'fieldValues(dataKey: MultipleValueDataKey): number[]',
    func: fieldValue,
  },
  {
    name: 'sum',
    signature: 'sum(...arr: number[]): number',
    func: sum,
  },
  {
    name: 'avg',
    signature: 'avg(...arr: number[]): number',
    func: avg,
  },
  {
    name: 'min',
    signature: 'min(...arr: number[]): number',
    func: min,
  },
  {
    name: 'max',
    signature: 'max(...arr: number[]): number',
    func: max,
  },
  {
    name: 'round',
    signature: 'round(arg: number): number',
    func: round,
  },
  {
    name: 'ceil',
    signature: 'ceil(arg: number): number',
    func: ceil,
  },
  {
    name: 'abs',
    signature: 'abs(arg: number): number',
    func: abs,
  },
  {
    name: 'floor',
    signature: 'floor(arg: number): number',
    func: floor,
  },
  {
    name: 'iswitch',
    signature: 'iswitch(...args: [boolean, number][])',
    func: iswitch,
  },
  {
    name: 'convertTimeValueTo',
    signature: "convertTimeValueTo(timeValueMs: number, period: 'seconds' | 'minutes' | 'hours'): number",
    func: convertTimeValueTo,
  },
];

function iif(value: boolean, trueValue: number, falseValue: number): number {
  return convertToBool(value) ? trueValue : falseValue;
}

function iswitch(...args: [boolean, number][]): number | null {
  // Exclude 'answers' and 'layout' from array of args
  args.splice(-3, 3);

  validateIswitchParams(...args);

  for (const [con, val] of args) {
    if (con === true) return val;
  }

  return null;
}

const validateIswitchParams = (...args: [boolean, number][]) => {
  for (let i = 0; i < args.length; i++) {
    if (args[i].length !== 2)
      throw new Error(i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.WrongLength'));

    const [con, val] = args[i];
    if (isNaN(val) || isNullOrEmpty(val.toString()))
      throw new Error(
        i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.IsNaN', {
          condition: i + 1,
        }),
      );

    if (typeof con !== 'boolean')
      throw new Error(
        i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.WrongCondition', {
          condition: i + 1,
        }),
      );
  }
};

function not(value: boolean): boolean {
  return !value;
}

function sum(...arr: number[]): number {
  // Exclude 'answers' and 'layout' from array of args
  arr.splice(-3, 3);
  return arr.flatMap((x) => x).reduce((cur, acc) => acc + cur, 0);
}

function avg(...arr: number[]): number {
  // Exclude 'answers' and 'layout' from array of args
  arr.splice(-3, 3);
  return sum(...arr.flatMap((x) => x)) / arr.length;
}

function min(...arr: number[]): number {
  // Exclude 'answers' and 'layout' from array of args
  arr.splice(-3, 3);
  return Math.min(...arr.flatMap((x) => x));
}

function max(...arr: number[]): number {
  // Exclude 'answers' and 'layout' from array of args
  arr.splice(-3, 3);
  return Math.max(...arr.flatMap((x) => x));
}

function round(arg: number): number {
  return Math.round(arg);
}

function ceil(arg: number): number {
  return Math.ceil(arg);
}

function floor(arg: number): number {
  return Math.floor(arg);
}

function abs(arg: number): number {
  return Math.abs(arg);
}

function convertTimeValueTo(timeValueMs: number, period: 'seconds' | 'minutes' | 'hours'): number {
  switch (period) {
    case 'seconds':
      return timeValueMs / 1000;

    case 'minutes':
      return timeValueMs / (1000 * 60);

    case 'hours':
      return timeValueMs / (1000 * 60 * 60);

    default:
      return timeValueMs;
  }
}

/**
 * NOTE: If you want answers and layout in a function, put it as the last argument
 */
function fieldValue(
  dataKey: string,
  answers: Record<string, any>,
  layout: SerializedNodes,
): number | undefined | (number | undefined)[] {
  const tuple = Object.entries(layout).filter(([, node]) => node.props.dataKey === dataKey)?.[0];

  if (!tuple)
    throw new Error(i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.DataKeyIsNotFound', { dataKey }));
  const [, node] = tuple;

  // @ts-ignore node.type.resolvedName
  const selectedValue = CalculatorControlsResolver[node.type.resolvedName](node, answers[dataKey]);

  if ((selectedValue === undefined || selectedValue === null) && node.props.isOptional) return node.props.noAnswerScore;

  return selectedValue;
}

function isEmpty(dataKey: string, answers: Record<string, any>, layout: SerializedNodes): boolean {
  const tuple = Object.entries(layout).filter(([, node]) => node.props.dataKey === dataKey)?.[0];

  if (!tuple)
    throw new Error(i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.DataKeyIsNotFound', { dataKey }));
  const [, node] = tuple;

  // @ts-ignore node.type.resolvedName
  const selectedValue = CalculatorControlsResolver[node.type.resolvedName](node, answers[dataKey]);

  if (
    Array.isArray(selectedValue) &&
    (!selectedValue.length || selectedValue.every((x) => x === null || x === undefined))
  )
    return true;

  return selectedValue === undefined || selectedValue === null;
}

function getVariable(
  name: string,
  answers: Record<string, any>,
  layout: SerializedNodes,
  variables: BaseVariableSection[],
): number {
  const variable = variables?.find((x) => x.name === name);
  if (!variable)
    throw new Error(i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.DataKeyIsNotFound', { name }));

  return executeSimpleExpression(variable.expression, answers, layout, variables);
}
